// Workflows (Runde 4, Agent 4C): Typen und Client-Funktionen nach crm/API.md, Abschnitt 19.1.
// Die Werte der Arten stehen auch im Backend (api/crm_api/workflows/konstanten.py).
import { http } from "./client";
import type { Liste, ListenParameter } from "./types";
import type { Ziel } from "./leadsNeu";

export type WorkflowStatus = "entwurf" | "aktiv" | "pausiert" | "archiviert";
export type LaufStatus = "wartet" | "laeuft" | "pausiert" | "fertig" | "fehler" | "abgebrochen";

export type AusloeserArt = "manuell" | "statuswechsel" | "lead_angelegt" | "custom_activity";
export type SchrittArt = "email_entwurf" | "sms" | "aufgabe" | "anrufaufgabe" | "zuweisen" | "lead_aendern" | "filter" | "verzoegerung";

export interface Ausloeser {
  art: AusloeserArt;
  /** statuswechsel: {von?: string[], nach: string[]} · custom_activity: {type_id} · sonst leer */
  config: Record<string, unknown>;
}

export interface Schritt {
  /** innerhalb des Workflows eindeutig, z. B. "s1" */
  id: string;
  art: SchrittArt;
  name: string | null;
  config: Record<string, unknown>;
}

export interface WorkflowEinstellungen {
  /** einmal: jeder Lead höchstens einmal im Workflow · mehrfach: erneutes Einschreiben möglich */
  empfaenger_modus?: "einmal" | "mehrfach";
  absender_user_id?: string | null;
  [weitere: string]: unknown;
}

export interface Workflow {
  id: string;
  name: string;
  beschreibung: string | null;
  status: WorkflowStatus;
  owner_id: string | null;
  owner_name?: string | null;
  ausloeser: Ausloeser;
  filter: WorkflowFilter;
  schritte: Schritt[];
  einstellungen: WorkflowEinstellungen;
  version: number;
  letzter_lauf_at: string | null;
  archiviert_at: string | null;
  date_created: string | null;
  date_updated: string | null;
  /** Was vor dem Aktivieren noch fehlt (leer = vollständig) */
  hinweise?: string[];
  /** Zähler der Läufe (Liste, Einzelabruf und nach Änderungen) */
  kennzahlen?: Kennzahlen;
}

export interface Kennzahlen {
  /** wartet oder läuft */
  aktiv: number;
  fertig: number;
  fehler: number;
  pausiert?: number;
  abgebrochen?: number;
  gesamt?: number;
}

export type Vergleich = "ist" | "ist_nicht" | "in" | "nicht_in" | "enthaelt" | "enthaelt_nicht" | "gefuellt" | "leer" | "groesser" | "kleiner";

/** Eine Bedingung: Feld ("status_id", "name", "kontakt.email", "kontakt.telefon", "alter_tage" oder "cf_…") und Vergleich. */
export interface Bedingung {
  feld: string;
  op: Vergleich;
  wert?: string | number | boolean | string[] | null;
}

export interface WorkflowFilter {
  bedingungen: Bedingung[];
}

export interface WorkflowEingabe {
  name: string;
  beschreibung?: string | null;
  ausloeser?: Ausloeser;
  filter?: Record<string, unknown>;
  schritte?: Schritt[];
  einstellungen?: WorkflowEinstellungen;
}

/** Entwurf aus einem E-Mail- oder SMS-Schritt (zum Öffnen im Komposer; gesendet wird nie von allein). */
export type Entwurf =
  | { art: "email"; an: string[]; contact_id: string | null; betreff: string; html: string; nutzer_id: string; fehlende: string[] }
  | { art: "sms"; contact_id: string; kontakt_name: string | null; telefon: string; text: string; nutzer_id: string; fehlende: string[] };

export interface LaufErgebnis {
  schritt_id: string;
  art: SchrittArt;
  status: "ok" | "fehler" | "uebersprungen";
  zeit: string;
  text: string | null;
  ref: string | null;
  /** Pflichtaufgabe, auf deren Erledigung der Lauf wartet */
  wartet_auf?: string;
  entwurf?: Entwurf;
  /** Nummer des Wiederholungsversuchs nach einem Netzfehler */
  wiederholung?: number;
  erledigt_at?: string;
}

export interface WorkflowLauf {
  id: string;
  workflow_id: string;
  workflow_name: string | null;
  workflow_version: number;
  lead_id: string;
  lead_name: string | null;
  contact_id: string | null;
  status: LaufStatus;
  ausloeser_art: AusloeserArt | null;
  ausloeser_ref: string | null;
  gestartet_von: string | null;
  schritt_index: number;
  schritt_gesamt: number;
  /** der Schritt, der als Nächstes drankommt (leer bei beendeten Läufen) */
  naechster_schritt?: { id: string; art: SchrittArt; name: string | null } | null;
  naechster_schritt_um: string | null;
  ergebnisse: LaufErgebnis[];
  fehler: string | null;
  gestartet_at: string | null;
  beendet_at: string | null;
}

export interface WorkflowListenParameter extends ListenParameter {
  status?: WorkflowStatus;
  q?: string;
  owner_id?: string;
}

export interface LaufListenParameter extends ListenParameter {
  workflow_id?: string;
  lead_id?: string;
  status?: LaufStatus;
}

export interface AusloesenErgebnis {
  eingeschrieben: number;
  uebersprungen: number;
  gruende: { lead_id: string; grund: string }[];
  /** Zahl der gewählten Leads */
  leads?: number;
  /** das Ziel hatte mehr Treffer als das Höchstmaß (2.000) */
  gekappt?: boolean;
  /** Antwort auf eine Vorschau: es wurde nichts eingeschrieben */
  vorschau?: boolean;
}

export interface ZielLaeufe {
  leads: number;
  leads_mit_laeufen: number;
  workflows: { workflow_id: string; name: string | null; aktiv: number; pausiert: number; fehler: number }[];
}

export const workflowsApi = {
  liste: (p: WorkflowListenParameter = {}) =>
    http.get<Liste<Workflow>>("/api/workflows", { status: p.status, q: p.q, owner_id: p.owner_id, limit: p.limit, cursor: p.cursor }),
  eins: (id: string) => http.get<{ workflow: Workflow }>(`/api/workflows/${encodeURIComponent(id)}`),
  anlegen: (e: WorkflowEingabe) => http.post<{ workflow: Workflow }>("/api/workflows", e),
  aendern: (id: string, e: Partial<WorkflowEingabe>) => http.patch<{ workflow: Workflow }>(`/api/workflows/${encodeURIComponent(id)}`, e),
  statusSetzen: (id: string, status: WorkflowStatus) => http.post<{ workflow: Workflow }>(`/api/workflows/${encodeURIComponent(id)}/status`, { status }),
  laeufe: (p: LaufListenParameter = {}) =>
    http.get<Liste<WorkflowLauf>>("/api/workflows/laeufe", { workflow_id: p.workflow_id, lead_id: p.lead_id, status: p.status, limit: p.limit, cursor: p.cursor }),
  laufAktion: (laufId: string, aktion: "pausieren" | "fortsetzen" | "abbrechen") =>
    http.post<{ lauf: WorkflowLauf }>(`/api/workflows/laeufe/${encodeURIComponent(laufId)}/aktion`, { aktion }),
  amLead: (leadId: string) => http.get<{ workflows: Workflow[]; laeufe: WorkflowLauf[] }>(`/api/workflows/lead/${encodeURIComponent(leadId)}`),
  /** Blitz ▾ "Workflow auslösen": Ziel wie bei der Massenbearbeitung (Smart View, Auswahl oder Leads-Liste) */
  ausloesen: (workflowId: string, ziel: Ziel, optionen: { vorschau?: boolean; bestaetigt?: boolean; contact_id?: string } = {}) =>
    http.post<AusloesenErgebnis>("/api/workflows/ausloesen", { workflow_id: workflowId, ...ziel, ...optionen }),
  duplizieren: (id: string) => http.post<{ workflow: Workflow }>(`/api/workflows/${encodeURIComponent(id)}/duplizieren`),
  /** Fällige Läufe sofort bearbeiten (sonst tut es der Zeitgeber alle 10 Sekunden) */
  verarbeiten: () => http.post<{ verarbeitet: number }>("/api/workflows/laeufe/verarbeiten"),
  /** Blitz ▾ "Workflows verwalten" an einer Smart View: welche Läufe gibt es für diese Leads */
  zielLaeufe: (ziel: Ziel) => http.post<ZielLaeufe>("/api/workflows/ziel-laeufe", ziel),
  zielAktion: (ziel: Ziel, aktion: "pausieren" | "fortsetzen" | "abbrechen", workflowId?: string) =>
    http.post<{ geaendert: number }>("/api/workflows/ziel-aktion", { ...ziel, aktion, workflow_id: workflowId }),
};
